import AppVersion from '@/components/app-version'
import ModeToggle from '@/components/landing/mood-toggle'
import { FullPageSpinner } from '@/components/loading_states/generic-spinner'
import { useAuth } from '@/components/providers/auth-provider'
import { AppSidebar } from '@/components/sidebar/app-sidebar'
import { hrAdminSidebarData } from '@/components/sidebar/sidebar-data'
import { Separator } from '@/components/ui/separator'
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { COLORS } from '@repo/shared_utils'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate, Outlet } from '@tanstack/react-router'
import { CalendarIcon } from 'lucide-react'
import { DateTime } from 'luxon'

export const Route = createFileRoute('/dashboard/hr_admin')({
  component: RouteComponent,
})

function RouteComponent() {
  const { user, isPending } = useAuth()
  const { data: currentDateTime } = useQuery({
    queryKey: ['current-time'],
    queryFn: () => DateTime.now(),
    refetchInterval: 60000, // refetch every minute (60 seconds)
    staleTime: 0,
  })

  if (isPending) return <FullPageSpinner />
  if (!user) return <Navigate to="/login" />
  // cubic review (2026-08-16) / Step A2 — admin and supervisor's parent layout routes
  // both redirect non-matching roles away; this one never did. Mirrors supervisor's
  // userRoles-array check (not admin's single user.role check) since it's the more
  // correct one for a multi-role user.
  if (!user.userRoles?.some((ur) => ur.role.name === 'hr_admin'))
    return <Navigate to="/dashboard" />

  return (
    <SidebarProvider>
      <AppSidebar {...{ ...hrAdminSidebarData, user: user }} />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12 bg-gradient-to-r from-background via-background/95 to-background/90 backdrop-blur-sm border-b border-border/50">
          <div className="flex w-full items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 dark:hover:from-blue-950/50 dark:hover:to-indigo-950/50 transition-all duration-200" />
            <Separator
              orientation="vertical"
              className="h-4 bg-gradient-to-b from-border to-border/50"
            />

            <Separator
              orientation="vertical"
              className="mr-2 h-4 bg-gradient-to-b from-border to-border/50"
            />

            <div className="ml-auto flex items-center space-x-3">
              <AppVersion />
              <div className="flex items-center space-x-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-gray-50/50 to-gray-100/50 dark:from-gray-900/50 dark:to-gray-800/50 backdrop-blur-sm border border-border/50">
                <CalendarIcon className="h-4 w-4" style={{ color: COLORS[1] }} />
                <div className="text-sm font-medium bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-transparent">
                  {currentDateTime?.toFormat('MMMM d, yyyy • h:mm a')}
                </div>
              </div>
              <div className="p-1 rounded-lg bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 backdrop-blur-sm border border-border/50">
                <ModeToggle />
              </div>
            </div>
          </div>
        </header>
        <div className="px-6 bg-gradient-to-br from-background via-background/95 to-background/90">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  )
}
